import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { Button } from "@/components/ui/Button";
import { AuthProvider, useAuth } from "@/features/auth/AuthProvider";

export function AppLayout() {
  return (
    <AuthProvider>
      <AppLayoutInner />
    </AuthProvider>
  );
}

function AppLayoutInner() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Collapse the mobile menu whenever the route changes — otherwise it
  // stays open over the new page, which is jarring.
  const closeMenu = () => setMenuOpen(false);

  const onLogout = async () => {
    await logout();
    closeMenu();
    navigate("/");
  };

  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-brand-100/80 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link
            to="/"
            className="flex items-center gap-2.5"
            onClick={closeMenu}
            aria-label={t("app.name")}
          >
            <img
              src="/brand/logo-128.png"
              srcSet="/brand/logo-64.png 1x, /brand/logo-128.png 2x, /brand/logo-256.png 3x"
              alt=""
              width={51}
              height={51}
              className="h-[51px] w-[51px] shrink-0 select-none"
              draggable={false}
            />
            <span className="text-base font-semibold tracking-tight brand-wordmark">
              {t("app.name")}
            </span>
          </Link>

          {/* Desktop nav — same as before, hidden below sm. */}
          <nav className="hidden items-center gap-4 text-sm sm:flex">
            <NavLinks
              user={user}
              onLogout={onLogout}
              onNavigate={closeMenu}
            />
          </nav>

          {/* Burger button — visible only below sm. */}
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 sm:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? t("nav.close_menu") : t("nav.open_menu")}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <BurgerIcon open={menuOpen} />
          </button>
        </div>

        {/* Mobile menu panel. Renders below the header on small screens
            when open; collapses to nothing otherwise so layout stays
            tight. We use `key={location.pathname}` on the inner content
            so React tears it down on every nav, which is a cheap way to
            make sure no in-progress state leaks. */}
        {menuOpen && (
          <nav
            id="mobile-nav"
            className="border-t bg-white px-4 py-3 sm:hidden"
            key={location.pathname}
          >
            <div className="flex flex-col gap-3 text-sm">
              <NavLinks
                user={user}
                onLogout={onLogout}
                onNavigate={closeMenu}
                stacked
              />
            </div>
          </nav>
        )}
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function NavLinks({
  user,
  onLogout,
  onNavigate,
  stacked = false,
}: {
  user: { name?: string; email: string } | null;
  onLogout: () => void;
  onNavigate: () => void;
  stacked?: boolean;
}) {
  const { t } = useTranslation();
  const linkClass = stacked
    ? "py-1 text-slate-700 hover:text-slate-900"
    : "text-slate-600 hover:text-slate-900";

  if (user) {
    return (
      <>
        <Link to="/dashboard" className={linkClass} onClick={onNavigate}>
          {t("nav.dashboard", "Dashboard")}
        </Link>
        <Link to="/facilities" className={linkClass} onClick={onNavigate}>
          {t("nav.facilities", "Facilities")}
        </Link>
        <span
          className={stacked ? "text-xs text-slate-500" : "text-slate-500"}
        >
          {user.name || user.email}
        </span>
        <div className={stacked ? "flex items-center justify-between gap-3" : "contents"}>
          <LocaleSwitcher />
          <Button size="sm" variant="ghost" onClick={onLogout}>
            {t("nav.logout", "Sign out")}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Link to="/login" className={linkClass} onClick={onNavigate}>
        {t("nav.login", "Sign in")}
      </Link>
      <Link to="/register" className={linkClass} onClick={onNavigate}>
        {t("nav.register", "Register")}
      </Link>
      <LocaleSwitcher />
    </>
  );
}

function BurgerIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg
        aria-hidden
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="h-5 w-5"
      >
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    );
  }
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="h-5 w-5"
    >
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
